import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap, Globe2, Users, Building2, Heart, ArrowRight,
  Clock, Wifi, BellRing,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Section, SectionHeading, Eyebrow } from "@/components/blocks";
import BuyButton from "@/components/BuyButton";
import WorkshopInterestDialog from "@/components/WorkshopInterestDialog";
import Testimonials from "@/components/Testimonials";
import api from "@/lib/api";

const PATHWAYS = [
  { icon: GraduationCap, title: "Future Teachers", desc: "Placement preparation, training, resources and employability support.", to: "/future-teachers", color: "bg-teal-600" },
  { icon: Globe2, title: "International Teachers", desc: "School culture, communication, confidence and successful transition into education systems.", to: "/international-teachers", color: "bg-emerald-600" },
  { icon: Users, title: "Mentor Teachers", desc: "Resources and professional learning to support the next generation of educators.", to: "/mentors-schools", color: "bg-slate-800" },
  { icon: Building2, title: "Schools & Universities", desc: "Partnership development, placement support and professional learning.", to: "/universities", color: "bg-teal-700" },
  { icon: Heart, title: "Families & Tutors", desc: "Tutoring, language learning and educational support.", to: "/tutoring", color: "bg-emerald-700" },
];

const REGIONS = ["Australia", "New Zealand", "Canada", "UK", "USA"];

export default function Home() {
  const [services, setServices] = useState([]);
  const [workshops, setWorkshops] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/workshops").then((r) => setWorkshops(r.data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-slate-900 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1574130303188-31a915382726?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="Teacher mentoring students"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-slate-900/70 to-teal-900/60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-3xl tp-fade-up">
            <div className="flex flex-wrap gap-2 mb-6">
              {REGIONS.map((r) => (
                <span key={r} className="text-[11px] font-semibold uppercase tracking-wider text-teal-300 bg-teal-500/10 border border-teal-500/30 rounded-full px-3 py-1">{r}</span>
              ))}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
              Teaching Pathways
            </h1>
            <p className="mt-5 text-xl sm:text-2xl font-display font-medium text-teal-300 leading-snug">
              Supporting Future Teachers. Strengthening Partnerships. Building Careers.
            </p>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-2xl">
              Supporting pre-service teachers, educators, mentor teachers, schools, universities and families through professional learning, mentoring, career support and educational partnerships.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-teal-600 hover:bg-teal-700 rounded-full px-6 h-12 text-base" data-testid="hero-cta-placement">
                <Link to="/future-teachers">Prepare for Placement <ArrowRight className="w-4 h-4" /></Link>
              </Button>
              <Button asChild size="lg" className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-6 h-12 text-base" data-testid="hero-cta-career">
                <Link to="/career-support">Career Support</Link>
              </Button>
              <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-6 h-12 text-base" data-testid="hero-cta-tutor">
                <Link to="/tutoring">Find a Tutor</Link>
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button asChild variant="outline" className="rounded-full border-slate-400/50 text-white bg-transparent hover:bg-white/10 hover:text-white" data-testid="hero-cta-schools">
                <Link to="/mentors-schools">For Schools</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full border-slate-400/50 text-white bg-transparent hover:bg-white/10 hover:text-white" data-testid="hero-cta-universities">
                <Link to="/universities">For Universities</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CHOOSE YOUR PATHWAY */}
      <Section>
        <SectionHeading eyebrow="Choose Your Pathway" title="One organisation. Every stage of the teaching journey." subtitle="From your first placement to your first classroom — and everyone who supports you along the way." />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PATHWAYS.map((p, i) => (
            <Link key={p.title} to={p.to} data-testid={`pathway-card-${i}`}
              className="group relative rounded-2xl border border-slate-200 bg-white p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden">
              <div className={`w-12 h-12 rounded-xl ${p.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                <p.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-display text-xl font-bold text-slate-900 mb-2">{p.title}</h3>
              <p className="text-slate-600 leading-relaxed text-[15px]">{p.desc}</p>
              <span className="mt-5 inline-flex items-center gap-1 text-teal-600 font-semibold text-sm group-hover:gap-2 transition-all">Explore <ArrowRight className="w-4 h-4" /></span>
            </Link>
          ))}
        </div>
      </Section>

      {/* MOST POPULAR SERVICES */}
      <div className="bg-slate-50 border-y border-slate-200">
        <Section>
          <SectionHeading eyebrow="Most Popular Services" title="Invest in your teaching career" subtitle="Trusted employability services, coaching and workshops that get results." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s) => (
              <Card key={s.id} className="p-6 flex flex-col border-slate-200 hover:border-teal-300 transition-colors" data-testid={`service-card-${s.id}`}>
                {s.badge && <Badge className="w-fit mb-3 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">{s.badge}</Badge>}
                <h3 className="font-display text-lg font-bold text-slate-900">{s.name}</h3>
                <p className="text-sm text-slate-600 mt-2 flex-1">{s.description}</p>
                <div className="mt-4 mb-4">
                  <span className="text-2xl font-bold text-slate-900">${s.price}</span>
                  {s.unit && <span className="text-sm text-slate-500"> {s.unit}</span>}
                </div>
                {s.price > 0 ? (
                  <BuyButton itemId={s.id} itemName={s.name} price={s.price} className="w-full bg-slate-900 hover:bg-teal-700 rounded-full" testId={`service-buy-${s.id}`} />
                ) : (
                  <Button asChild className="w-full rounded-full"><Link to="/career-support">Learn more</Link></Button>
                )}
              </Card>
            ))}
            <Card className="p-6 flex flex-col justify-center items-center text-center border-dashed border-2 border-teal-300 bg-teal-50/50" data-testid="service-card-marketplace">
              <Heart className="w-8 h-8 text-teal-600 mb-3" />
              <h3 className="font-display text-lg font-bold text-slate-900">Tutoring Marketplace</h3>
              <p className="text-sm text-slate-600 mt-2 mb-4">Find tutors and language educators for your family.</p>
              <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 rounded-full"><Link to="/tutoring">Browse tutors</Link></Button>
            </Card>
          </div>
        </Section>
      </div>

      {/* SUCCESS STORIES */}
      <Testimonials
        eyebrow="Success Stories"
        heading="Real outcomes from real educators"
        subtitle="What teachers say after working with us."
      />

      {/* UPCOMING WORKSHOPS */}
      <div className="bg-slate-900">
        <Section>
          <SectionHeading eyebrow="Workshops" title="Register your interest" subtitle="Live, online workshops designed to build confidence and capability at every stage. Tell us which sessions you want and we'll notify you first when dates are confirmed." light />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {workshops.map((w) => (
              <div key={w.id} className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between rounded-xl bg-slate-800/60 border border-slate-700 p-5 hover:border-teal-500 transition-colors" data-testid={`home-workshop-${w.id}`}>
                <div className="flex gap-4 items-start">
                  <div className="text-center bg-teal-600 rounded-lg px-3 py-2 shrink-0">
                    <BellRing className="w-5 h-5 text-white mx-auto" />
                  </div>
                  <div>
                    <h4 className="font-display font-semibold text-white">{w.title}</h4>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Dates to be announced</span>
                      <span className="flex items-center gap-1"><Wifi className="w-3 h-3" /> {w.mode}</span>
                    </div>
                  </div>
                </div>
                <WorkshopInterestDialog workshop={w} triggerClassName="bg-white text-slate-900 hover:bg-teal-50 rounded-full shrink-0" />
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button asChild variant="outline" className="rounded-full border-slate-600 text-white bg-transparent hover:bg-white/10 hover:text-white" data-testid="home-all-workshops">
              <Link to="/international-teachers">View all workshops <ArrowRight className="w-4 h-4" /></Link>
            </Button>
          </div>
        </Section>
      </div>
    </div>
  );
}
